import { IGeoJson2DGeographicCoordinates, LocationType } from '~/client/graph'

export type LocationDisplayResolvedBy =
  | 'tag'
  | 'ancestorTag'
  | 'displayed'
  | 'ancestorDisplayed'
  | 'projectTag'

export interface ILocationDisplayContext {
  coordinates: IGeoJson2DGeographicCoordinates | null
  globeViewId: string | null
  sitemapItemId: string | null
  resolvedLocationId: string | null
  resolvedBy: LocationDisplayResolvedBy | null
}

export interface ILocationNode {
  id: string
  type: LocationType
  assignedGlobes?: string[] | null
  parent?: { parentId?: string | null } | null
}

interface IGlobeNode {
  items?: Array<{ sitemapItemId?: string | null; isHidden?: boolean }>
}

interface ISitemapItemNode {
  assignedId?: string | null
  coordinates?: IGeoJson2DGeographicCoordinates | null
}

interface IResolveLocationDisplayContextDeps {
  locationId: string | null | undefined
  candidateGlobeIds: string[]
  projectLocationAssignedGlobes: string[]
  getLocation: (id: string) => ILocationNode | null | undefined
  getGlobe: (id: string) => IGlobeNode | null | undefined
  getSitemapItem: (id: string) => ISitemapItemNode | null | undefined
}

const EMPTY_CONTEXT: ILocationDisplayContext = {
  coordinates: null,
  globeViewId: null,
  sitemapItemId: null,
  resolvedLocationId: null,
  resolvedBy: null,
}

interface IDisplayedMatch {
  sitemapItemId: string
  coordinates: IGeoJson2DGeographicCoordinates | null
}

export function resolveLocationDisplayContext(
  deps: IResolveLocationDisplayContextDeps,
): ILocationDisplayContext {
  const {
    locationId,
    candidateGlobeIds,
    projectLocationAssignedGlobes,
    getLocation,
    getGlobe,
    getSitemapItem,
  } = deps

  if (!locationId || !candidateGlobeIds.length) {
    return EMPTY_CONTEXT
  }

  const projectTagSet = new Set(projectLocationAssignedGlobes ?? [])
  const nonProjectCandidates = candidateGlobeIds.filter(
    g => !projectTagSet.has(g),
  )
  // Filter dead globe ids: this tier is the terminal fallback with no
  // downstream validation. Earlier tiers funnel through selectGlobeById,
  // which already guards against missing ids.
  const projectTaggedCandidate =
    candidateGlobeIds.find(g => projectTagSet.has(g) && !!getGlobe(g)) ?? null

  const tagMatch = walkHierarchy(locationId, getLocation, (node, isLeaf) => {
    const match = nonProjectCandidates.find(g =>
      node.assignedGlobes?.includes(g),
    )
    if (!match) {
      return null
    }
    // Tag tiers pick the globe; coord comes from the displayed walk below.
    return buildContext({
      globeViewId: match,
      resolvedLocationId: node.id,
      info: null,
      resolvedBy: isLeaf ? 'tag' : 'ancestorTag',
    })
  })
  if (tagMatch) {
    return tagMatch
  }

  const displayedMatch = walkHierarchy(
    locationId,
    getLocation,
    (node, isLeaf) => {
      for (const globeId of candidateGlobeIds) {
        const info = findDisplayedItem(
          globeId,
          node.id,
          getGlobe,
          getSitemapItem,
        )
        if (info) {
          return buildContext({
            globeViewId: globeId,
            resolvedLocationId: node.id,
            info,
            resolvedBy: isLeaf ? 'displayed' : 'ancestorDisplayed',
          })
        }
      }
      return null
    },
  )
  if (displayedMatch) {
    return displayedMatch
  }

  if (projectTaggedCandidate) {
    return {
      coordinates: null,
      globeViewId: projectTaggedCandidate,
      sitemapItemId: null,
      resolvedLocationId: null,
      resolvedBy: 'projectTag',
    }
  }

  return EMPTY_CONTEXT
}

interface IWalkHierarchyOptions {
  /**
   * When true (default), the walk terminates before visiting any
   * ProjectLocation ancestor — matches the picker cascade where the
   * root location is handled separately via tier 5. Pass `false` to
   * include Project ancestors (e.g. LBS-tag lookups that treat the
   * Project node as a legitimate match).
   */
  stopBeforeProject?: boolean
}

export interface IAncestorCoordinates {
  coordinates: IGeoJson2DGeographicCoordinates
  resolvedLocationId: string
}

interface IResolveLocationCoordinatesDeps {
  locationId: string | null | undefined
  getLocation: (id: string) => ILocationNode | null | undefined
  getSitemapItemByLocationId: (
    id: string,
  ) => ISitemapItemNode | null | undefined
  /**
   * When `true` (default), ProjectLocation is a valid ancestor — used by
   * Map Setup's pending-placement seed where the project address is a
   * legitimate fallback. Pass `false` when the resolved coord is rendered
   * as "parent location" context (e.g. the Precise Location picker's
   * inherited hint), since inheriting from the project root implies a
   * parent-of relationship that doesn't exist for top-level Locations.
   */
  includeProject?: boolean
}

/**
 * Walks the Location's parent chain (including self) and returns the first
 * ancestor whose SitemapItem carries coordinates. Globe-agnostic: a
 * Location's lat/lng lives on the shared SitemapItem, so visibility on any
 * particular globe is irrelevant. Returns null when no ancestor in scope
 * has coords.
 */
export function resolveLocationCoordinates(
  deps: IResolveLocationCoordinatesDeps,
): IAncestorCoordinates | null {
  const {
    locationId,
    getLocation,
    getSitemapItemByLocationId,
    includeProject = true,
  } = deps
  if (!locationId) {
    return null
  }
  return walkHierarchy<IAncestorCoordinates>(
    locationId,
    getLocation,
    node => {
      const sitemapItem = getSitemapItemByLocationId(node.id)
      if (!sitemapItem?.coordinates) {
        return null
      }
      return {
        coordinates: sitemapItem.coordinates,
        resolvedLocationId: node.id,
      }
    },
    { stopBeforeProject: !includeProject },
  )
}

/**
 * Shared cycle-guarded ancestor walk. `visit` receives `isLeaf=true`
 * only for the starting node; a truthy return terminates the walk.
 */
export function walkHierarchy<T>(
  startId: string,
  getLocation: (id: string) => ILocationNode | null | undefined,
  visit: (node: ILocationNode, isLeaf: boolean) => T | null,
  { stopBeforeProject = true }: IWalkHierarchyOptions = {},
): T | null {
  const visited = new Set<string>()
  let currentId: string | null = startId
  let isLeaf = true
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId)
    const node = getLocation(currentId)
    if (!node) {
      return null
    }
    if (
      stopBeforeProject &&
      !isLeaf &&
      node.type === LocationType.ProjectLocation
    ) {
      return null
    }
    const result = visit(node, isLeaf)
    if (result) {
      return result
    }
    currentId = node.parent?.parentId ?? null
    isLeaf = false
  }
  return null
}

function findDisplayedItem(
  globeId: string,
  locationId: string,
  getGlobe: (id: string) => IGlobeNode | null | undefined,
  getSitemapItem: (id: string) => ISitemapItemNode | null | undefined,
): IDisplayedMatch | null {
  const globe = getGlobe(globeId)
  if (!globe?.items) {
    return null
  }
  for (const item of globe.items) {
    if (item.isHidden || !item.sitemapItemId) {
      continue
    }
    const sitemapItem = getSitemapItem(item.sitemapItemId)
    if (sitemapItem?.assignedId === locationId) {
      return {
        sitemapItemId: item.sitemapItemId,
        coordinates: sitemapItem.coordinates ?? null,
      }
    }
  }
  return null
}

function buildContext({
  globeViewId,
  resolvedLocationId,
  info,
  resolvedBy,
}: {
  globeViewId: string
  resolvedLocationId: string
  info: IDisplayedMatch | null
  resolvedBy: LocationDisplayResolvedBy
}): ILocationDisplayContext {
  return {
    coordinates: info?.coordinates ?? null,
    globeViewId,
    sitemapItemId: info?.sitemapItemId ?? null,
    resolvedLocationId,
    resolvedBy,
  }
}
